import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';

import SidebarContainer from '../../../containers/sidebar-container';

import Header from '../../header';
import ButtonsBar from '../../buttons-bar';
import Hidden from '../components/hidden';
import ColumnContainer from '../components/column-container';
import { useScrollPosition, useWindowSize } from '../../../lib/hooks';
import { HOMEWORK_EXECUTION, PAUSE, RESULTS, START_PAGE } from '../../../constants/routes';

import { Route, Routes } from 'react-router-dom';

import { isChromeOnIos, isFirefoxOnAndroid } from '../../../lib/utils';
import FluidContainer from '../components/fluid-container';

export const parseAuthors = (authors) => {
    let authorsList = '';
    if (authors) {
        authors.forEach((item) => {
            authorsList = authorsList.concat(`${item.name} ${item.surname}, `);
        });
        authorsList = authorsList.substring(0, authorsList.length - 2);
    }
    return authorsList;
};

export const ButtonsBarContainer = styled.div`
    height: 56px;
    position: fixed;
    bottom: ${(props) => (props.visible ? '0px' : '-56px')};
    right: 0;

    transition: bottom 500ms ease-in-out;
`;

export const DesktopButtonsBar = ({ exercise }) => (
    <FluidContainer position="fixed" right={0} bottom={0} zIndex={1}>
        <ColumnContainer width={[0, 0, 2 / 12]} />
        <ColumnContainer width={[1, 1, 10 / 12]}>
            <Routes>
                <Route path={PAUSE} element={<ButtonsBar exercise={exercise} />} />
                <Route path={RESULTS} element={<ButtonsBar exercise={exercise} />} />
                <Route path={START_PAGE} element={<ButtonsBar exercise={exercise} />} />
                <Route path={HOMEWORK_EXECUTION} element={<ButtonsBar exercise={exercise} />} />
            </Routes>
        </ColumnContainer>
    </FluidContainer>
);

DesktopButtonsBar.propTypes = {
    exercise: PropTypes.any,
};

export const MobileButtonsBar = ({ exercise }) => (
    <ColumnContainer>
        <ColumnContainer width={[0, 0, 2 / 12]} />
        <ColumnContainer width={[1, 1, 10 / 12]}>
            <Routes>
                <Route path={PAUSE} element={<ButtonsBar exercise={exercise} />} />
                <Route path={RESULTS} element={<ButtonsBar exercise={exercise} />} />
                <Route path={START_PAGE} element={<ButtonsBar exercise={exercise} />} />
                <Route path={HOMEWORK_EXECUTION} element={<ButtonsBar exercise={exercise} />} />
            </Routes>
        </ColumnContainer>
    </ColumnContainer>
);

MobileButtonsBar.propTypes = {
    exercise: PropTypes.any,
};

const DefaultLayout = ({
    authors,
    courseTitle,
    children,
    exercises = [],
    homeworkStatus = {},
    isLoading,
    profile = {},
    publicTitle,
    status,
    tick,
    terminateHomework,
    showModal,
    errorLayout = false,
}) => {
    const { innerHeight } = useWindowSize();
    const { y: position } = useScrollPosition();
    const [currentPositionY, setCurrentPositionY] = useState(0);
    const [buttonsBarVisible, setButtonsBarVisible] = useState(true);
    const changeVisibilityThreshold = 3; //px

    useEffect(() => {
        setCurrentPositionY(position);

        if (position - currentPositionY > changeVisibilityThreshold) {
            setButtonsBarVisible(false);
        } else if (
            currentPositionY - position > changeVisibilityThreshold ||
            position <= 0 ||
            Math.ceil(innerHeight + position) >= document.body.clientHeight
        ) {
            setButtonsBarVisible(true);
        }
    }, [position, currentPositionY, innerHeight]);

    return (
        <ColumnContainer>
            <Hidden print={true}>
                <Header
                    authors={parseAuthors(authors)}
                    book={courseTitle}
                    homeworkStatus={homeworkStatus}
                    numberOfExercises={exercises.length}
                    title={publicTitle}
                    type={profile.name}
                    homeworkTime={profile.time}
                    tick={tick}
                    terminateHomework={terminateHomework}
                    showModal={showModal}
                    applicationStatus={profile.status}
                    isLoading={isLoading}
                    source={profile.source}
                />
            </Hidden>
            <Hidden below="desktop">
                <ColumnContainer width={[0, 0, 2 / 12]} position="fixed" top="80px" zIndex={3}>
                    <div
                        style={{
                            width: '100%',
                            backgroundColor: 'surface-grey-050',
                        }}
                    >
                        <SidebarContainer />
                    </div>
                </ColumnContainer>
            </Hidden>
            <ColumnContainer width={[0, 0, 3 / 12]} />
            <ColumnContainer width={[4 / 4, 8 / 8, 8 / 12]} paddingBottom={120} paddingTop={24}>
                <FluidContainer paddingX={['mobile', 'tablet', 'desktop']}>{children}</FluidContainer>
            </ColumnContainer>
            {!errorLayout && (
                <>
                    <Hidden above="tablet" print={true}>
                        {isChromeOnIos() || isFirefoxOnAndroid() ? (
                            <DesktopButtonsBar exercise={exercises[status.currentExerciseIndex]} status={status} />
                        ) : (
                            <ButtonsBarContainer visible={buttonsBarVisible}>
                                <MobileButtonsBar exercise={exercises[status.currentExerciseIndex]} status={status} />
                            </ButtonsBarContainer>
                        )}
                    </Hidden>
                    <Hidden below="tablet" print={true}>
                        <DesktopButtonsBar exercise={exercises[status.currentExerciseIndex]} status={status} />
                    </Hidden>
                </>
            )}
        </ColumnContainer>
    );
};

DefaultLayout.propTypes = {
    authors: PropTypes.array,
    courseTitle: PropTypes.string,
    children: PropTypes.node.isRequired,
    exercises: PropTypes.any,
    errorLayout: PropTypes.bool,
    homeworkStatus: PropTypes.object,
    isLoading: PropTypes.bool,
    profile: PropTypes.object,
    publicTitle: PropTypes.string,
    showModal: PropTypes.func,
    status: PropTypes.object,
    tick: PropTypes.func,
    terminateHomework: PropTypes.func,
};

export default DefaultLayout;
